import React, { useState, useEffect } from 'react';
import { Trash2, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const RemovePages = () => {
    const [file, setFile] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [pagesToRemove, setPagesToRemove] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    // Step 1: Upload PDF
    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPdfDoc(pdf);
                setTotalPages(pdf.getPageCount());
                setPagesToRemove([]);
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    // Step 3: Select pages to remove
    const togglePageSelection = (pageNum) => {
        setPagesToRemove(prev => {
            if (prev.includes(pageNum)) {
                return prev.filter(p => p !== pageNum);
            } else {
                return [...prev, pageNum].sort((a, b) => a - b);
            }
        });
    };

    const selectAllPages = () => {
        setPagesToRemove(Array.from({ length: totalPages }, (_, i) => i + 1));
    };

    const deselectAllPages = () => {
        setPagesToRemove([]);
    };

    // Step 4: Download modified PDF
    const removeSelectedPages = async () => {
        if (pagesToRemove.length === 0) {
            toast.warning('Please select at least one page to remove');
            return;
        }

        if (pagesToRemove.length === totalPages) {
            toast.error('Cannot remove all pages. At least one page must remain.');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            // Create a new PDF document
            const newPdf = await PDFDocument.create();
            setProgress(30);

            // Copy pages that are NOT in the removal list
            const pagesToKeep = [];
            for (let i = 1; i <= totalPages; i++) {
                if (!pagesToRemove.includes(i)) {
                    pagesToKeep.push(i - 1); // Convert to 0-indexed
                }
            }

            setProgress(50);

            // Copy the pages we want to keep
            const copiedPages = await newPdf.copyPages(pdfDoc, pagesToKeep);
            copiedPages.forEach(page => newPdf.addPage(page));

            setProgress(80);

            // Save the PDF
            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            // Set preview
            const filename = `removed-pages-${file.name}`;
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success(`${pagesToRemove.length} page(s) removed! Opening preview...`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Remove error:', error);
            toast.error(`Failed to remove pages: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate live preview when pages to remove change
    const generateLivePreview = async () => {
        if (!pdfDoc || pagesToRemove.length === 0 || pagesToRemove.length === totalPages) {
            setPreviewBlob(null);
            return;
        }

        try {
            const newPdf = await PDFDocument.create();
            const pagesToKeep = [];
            for (let i = 1; i <= totalPages; i++) {
                if (!pagesToRemove.includes(i)) {
                    pagesToKeep.push(i - 1);
                }
            }

            const copiedPages = await newPdf.copyPages(pdfDoc, pagesToKeep);
            copiedPages.forEach(page => newPdf.addPage(page));

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const filename = `removed-pages-${file.name}`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when selection changes
    useEffect(() => {
        if (file && pdfDoc) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [pagesToRemove, file, pdfDoc, totalPages]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Trash2 size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Remove Pages
                </h1>
                <p className="tool-description">
                    Remove unwanted pages from your PDF. Select the pages you want to delete and download the modified document.
                </p>
            </div>

            {/* Step 1: File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{totalPages} pages</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPdfDoc(null);
                                setTotalPages(0);
                                setPagesToRemove([]);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Step 2 & 3: Display page thumbnails and Select pages to remove */}
            {file && (
                <>
                    <Card>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                <h3 style={{
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: 'var(--font-bold)'
                                }}>
                                    Select Pages to Remove
                                </h3>
                                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                    <Button variant="ghost" size="sm" onClick={selectAllPages}>
                                        Select All
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={deselectAllPages}>
                                        Deselect All
                                    </Button>
                                </div>
                            </div>

                            <NoteCard variant="warning">
                                <strong>Note:</strong> Click on pages to mark them for removal. Selected pages will be deleted from the PDF.
                            </NoteCard>

                            {/* Page Thumbnails Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                gap: 'var(--spacing-4)'
                            }}>
                                {Array.from({ length: totalPages }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isSelected = pagesToRemove.includes(pageNum);

                                    return (
                                        <div
                                            key={pageNum}
                                            onClick={() => togglePageSelection(pageNum)}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'all var(--transition-base)'
                                            }}
                                        >
                                            <Card
                                                variant={isSelected ? 'elevated' : 'outlined'}
                                                style={{
                                                    border: isSelected
                                                        ? '2px solid var(--color-error)'
                                                        : '1px solid var(--color-border)',
                                                    backgroundColor: isSelected
                                                        ? 'rgba(239, 68, 68, 0.1)'
                                                        : 'var(--color-surface)',
                                                    position: 'relative'
                                                }}
                                            >
                                                {/* Page Thumbnail Placeholder */}
                                                <div style={{
                                                    aspectRatio: '8.5/11',
                                                    backgroundColor: 'var(--color-surface-elevated)',
                                                    borderRadius: 'var(--radius-md)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: 'var(--spacing-2)',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}>
                                                    {/* Page Number */}
                                                    <div style={{
                                                        fontSize: 'var(--text-3xl)',
                                                        fontWeight: 'var(--font-bold)',
                                                        color: isSelected ? 'var(--color-error)' : 'var(--color-text-tertiary)',
                                                        opacity: isSelected ? 0.5 : 1
                                                    }}>
                                                        {pageNum}
                                                    </div>

                                                    {/* Deletion Indicator */}
                                                    {isSelected && (
                                                        <>
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <Trash2 size={32} color="var(--color-error)" />
                                                            </div>
                                                            <Badge
                                                                variant="error"
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 'var(--spacing-2)',
                                                                    right: 'var(--spacing-2)'
                                                                }}
                                                            >
                                                                Remove
                                                            </Badge>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Page Label */}
                                                <div style={{
                                                    textAlign: 'center',
                                                    fontSize: 'var(--text-sm)',
                                                    fontWeight: 'var(--font-medium)',
                                                    color: isSelected ? 'var(--color-error)' : 'var(--color-text-primary)'
                                                }}>
                                                    Page {pageNum}
                                                </div>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Selection Summary */}
                            {pagesToRemove.length > 0 && (
                                <Alert variant="error" style={{ marginTop: 'var(--spacing-4)' }}>
                                    <strong>{pagesToRemove.length} page{pagesToRemove.length !== 1 ? 's' : ''} selected for removal</strong>
                                    <div style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--text-sm)' }}>
                                        Pages to remove: {pagesToRemove.join(', ')}
                                    </div>
                                    <div style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--text-sm)' }}>
                                        Remaining pages: {totalPages - pagesToRemove.length}
                                    </div>
                                </Alert>
                            )}
                        </Card>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <ProgressBar value={progress} striped showLabel />
                    )}

                    {/* Step 4: Preview and Download Buttons */}
                    <div className="button-group" style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="danger"
                            size="lg"
                            onClick={removeSelectedPages}
                            disabled={isProcessing || pagesToRemove.length === 0}
                            loading={isProcessing}
                            style={{ flex: 1 }}
                        >
                            <Trash2 size={20} />
                            Remove {pagesToRemove.length} Page{pagesToRemove.length !== 1 ? 's' : ''} & Download
                        </Button>
                        <Button
                            variant="ghost"
                            size="lg"
                            onClick={deselectAllPages}
                            disabled={isProcessing || pagesToRemove.length === 0}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setFile(null);
                            setPdfDoc(null);
                            setTotalPages(0);
                            setPagesToRemove([]);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default RemovePages;
